import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, responsiveFontSizes, ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import axios from 'axios';

import Setup           from './components/Setup';
import Login           from './components/Login';
import Layout          from './components/Layout';
import UserManagement  from './components/UserManagement';
import ChurchSettings  from './components/ChurchSettings';
import IncomeTypes     from './components/IncomeTypes';
import ExpenseTypes    from './components/ExpenseTypes';
import BankAccounts    from './components/BankAccounts';
import CashBook        from './components/CashBook';
import Reports         from './components/Reports';
import InstallPrompt   from './components/InstallPrompt';

import { NotifierProvider } from './notify';
import { setupStatusURL } from './BaseURL';

let theme = createTheme({
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
  palette: {
    primary:    { main: '#0B2545', light: '#13315C', dark: '#061529', contrastText: '#fff' },
    secondary:  { main: '#C89211', light: '#E0A82E', dark: '#8F6600', contrastText: '#fff' },
    success:    { main: '#0F766E' },
    error:      { main: '#B91C1C' },
    warning:    { main: '#C89211' },
    background: { default: '#F5F7FA', paper: '#ffffff' },
    text:       { primary: '#0F172A', secondary: '#475569' },
    divider:    '#E2E8F0',
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button:    { fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
          border: '1px solid #E2E8F0',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F1F5F9',
            color: '#0B2545',
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            borderBottom: '1px solid #CBD5E1',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': { borderColor: '#CBD5E1' },
          '&:hover fieldset': { borderColor: '#94A3B8' },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiDialog: {
      defaultProps: { fullWidth: true },
      styleOverrides: {
        paper: {
          margin: 16,
          width: 'calc(100% - 32px)',
          maxHeight: 'calc(100% - 32px)',
          '@media (max-width:600px)': {
            margin: 8,
            width: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px)',
            borderRadius: 12,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { padding: '16px 20px' },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { padding: '16px 20px' },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { padding: '12px 16px', gap: 8 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #F1F5F9',
          '@media (max-width:600px)': {
            padding: '10px 8px',
            fontSize: 12.5,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, borderRadius: 8, boxShadow: 'none',
          '@media (max-width:600px)': { minHeight: 40 },
        },
        containedPrimary: {
          '&:hover': { boxShadow: '0 4px 12px rgba(11,37,69,0.24)' },
        },
      },
    },
  },
});
theme = responsiveFontSizes(theme, { factor: 3 });

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('church_user')); } catch { return null; }
}

function PrivateRoute({ children, roles, permission }) {
  const token = localStorage.getItem('church_token');
  if (!token) return <Navigate to="/login" />;
  const user = getStoredUser();
  if (!user) return <Navigate to="/login" />;
  if (roles && roles.length && !roles.includes(user.role)) return <Navigate to="/" />;
  if (permission && user.role !== 'superadmin' && !user.permissions?.[permission]) {
    return <Navigate to="/" />;
  }
  return children;
}

function Splash() {
  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg, #0B2545 0%, #13315C 60%, #061529 100%)',
    }}>
      <CircularProgress sx={{ color: '#fff' }} />
    </Box>
  );
}

export default function App() {
  const [user, setUser]         = useState(null);
  const [status, setStatus]     = useState({ loading: true, setup_done: false, church: null });

  const refreshStatus = async () => {
    try {
      const res = await axios.get(setupStatusURL);
      setStatus({ loading: false, setup_done: !!res.data.setup_done, church: res.data.church });
    } catch {
      setStatus({ loading: false, setup_done: false, church: null });
    }
  };

  useEffect(() => {
    const stored = getStoredUser();
    if (stored) setUser(stored);
    refreshStatus();
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('church_token', token);
    localStorage.setItem('church_user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('church_token');
    localStorage.removeItem('church_user');
    setUser(null);
  };

  const handleChurchInfoChange = (info) => {
    setStatus(s => ({ ...s, church: info }));
  };

  if (status.loading) return <ThemeProvider theme={theme}><CssBaseline /><Splash /></ThemeProvider>;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotifierProvider>
      <Router>
        {!status.setup_done ? (
          <Setup onDone={refreshStatus} />
        ) : (
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} churchInfo={status.church} />} />
            <Route
              path="/*"
              element={
                <PrivateRoute>
                  <Layout user={user} churchInfo={status.church} onLogout={handleLogout}>
                    <Routes>
                      <Route path="/"              element={<PrivateRoute permission="cash_book"><CashBook /></PrivateRoute>} />
                      <Route path="/income-types"  element={<PrivateRoute permission="income_types"><IncomeTypes /></PrivateRoute>} />
                      <Route path="/expense-types" element={<PrivateRoute permission="expense_types"><ExpenseTypes /></PrivateRoute>} />
                      <Route path="/bank-accounts" element={<PrivateRoute permission="bank_accounts"><BankAccounts /></PrivateRoute>} />
                      <Route path="/reports"       element={<PrivateRoute permission="reports"><Reports /></PrivateRoute>} />
                      <Route path="/users"         element={<PrivateRoute permission="users" roles={['superadmin','admin']}><UserManagement /></PrivateRoute>} />
                      <Route path="/settings"      element={<PrivateRoute permission="settings" roles={['superadmin','admin']}><ChurchSettings onSaved={handleChurchInfoChange} /></PrivateRoute>} />
                      <Route path="*"              element={<Navigate to="/" replace />} />
                    </Routes>
                  </Layout>
                </PrivateRoute>
              }
            />
          </Routes>
        )}
      </Router>
      <InstallPrompt />
      </NotifierProvider>
    </ThemeProvider>
  );
}
